import { ComponentProps, useMemo } from "react";

import { Clickable } from "@design-system/Clickable/Clickable";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Option, Select } from "@design-system/Select/Select";
import { Text } from "@design-system/Text/Text";

import classes from "./Pagination.module.css";

const PAGE_SIZES: Option[] = [
  {
    id: "10",
    label: "10",
  },
  {
    id: "20",
    label: "20",
  },
  {
    id: "30",
    label: "30",
  },
  {
    id: "40",
    label: "40",
  },
  {
    id: "50",
    label: "50",
  },
] as const;

type Props = {
  pageCount: number;
  currentPage: number;
  rowCount: number;
  onPageClick: (page: number) => void;
  onNextClick: () => void;
  onPreviousClick: () => void;
  pageSize: number;
  canNextPage: boolean;
  canPreviousPage: boolean;
  currentPageSize: number;
  pageOptions: number[];
  onPageSizeChange: (pageSize: number) => void;
  showPageSizeDropdown?: boolean;
};

export const Pagination = ({
  currentPage,
  rowCount,
  onPageClick,
  onNextClick,
  onPreviousClick,
  pageSize,
  canNextPage,
  canPreviousPage,
  currentPageSize,
  pageOptions,
  onPageSizeChange,
  showPageSizeDropdown,
}: Props) => {
  // Generate pages to show with smart pagination including first/last pages and ellipsis
  const paginationItems = useMemo(() => {
    const totalPages = pageOptions.length;
    const items: Array<{
      type: "page" | "ellipsis";
      page?: number;
      key: string;
    }> = [];

    // Show all pages if we have 11 or fewer (4 first + 3 middle + 4 last)
    if (totalPages <= 11) {
      pageOptions.forEach((page) => {
        items.push({ type: "page", page, key: `page-${page}` });
      });
      return items;
    }

    // Helper function to add a page if it hasn't been added yet
    const addPage = (page: number) => {
      if (!items.some((item) => item.type === "page" && item.page === page)) {
        items.push({ type: "page", page, key: `page-${page}` });
      }
    };

    // Helper function to add ellipsis if needed
    const addEllipsis = (key: string) => {
      if (items[items.length - 1]?.type !== "ellipsis") {
        items.push({ type: "ellipsis", key });
      }
    };

    const firstPagesToShow = 4; // Show first 4 pages
    const lastPagesToShow = 4; // Show last 4 pages
    const delta = 1; // How many pages to show on each side of current page

    // Add first 4 pages
    for (let i = 0; i < Math.min(firstPagesToShow, totalPages); i++) {
      addPage(i);
    }

    // Calculate middle range around current page
    const middleStart = Math.max(firstPagesToShow, currentPage - delta);
    const middleEnd = Math.min(totalPages - lastPagesToShow - 1, currentPage + delta);

    // Add ellipsis before middle range if there's a gap
    if (middleStart > firstPagesToShow) {
      addEllipsis("ellipsis-start");
    }

    // Add pages in the middle range
    for (let i = middleStart; i <= middleEnd; i++) {
      addPage(i);
    }

    // Add ellipsis after middle range if there's a gap
    if (middleEnd < totalPages - lastPagesToShow - 1) {
      addEllipsis("ellipsis-end");
    }

    // Add last 4 pages
    for (let i = Math.max(totalPages - lastPagesToShow, firstPagesToShow); i < totalPages; i++) {
      addPage(i);
    }

    return items;
  }, [pageOptions, currentPage]);

  const handlePageSizeChange: ComponentProps<typeof Select>["onChange"] = (
    id,
  ) => {
    onPageSizeChange(parseInt(id as string));
  };

  const isEmpty = currentPageSize === 0;

  return (
    <div className={classes.container}>
      <div className={classes.left}>
        {!isEmpty && showPageSizeDropdown && (
          <div className={classes.pageSize}>
            <Select
              value={pageSize.toString()}
              options={PAGE_SIZES}
              onChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>
      <div className={classes.center}>
        {canPreviousPage && (
          <IconButton icon="faChevronLeft" onClick={onPreviousClick} />
        )}
        {paginationItems.map((item) =>
          item.type === "ellipsis" ? (
            <div key={item.key} className={classes.ellipsis}>
              <Text color="textSecondary">...</Text>
            </div>
          ) : (
            <Clickable
              key={item.key}
              size={32}
              center
              onClick={() => onPageClick(item.page!)}
            >
              <Text
                color={item.page === currentPage ? "primary" : "textSecondary"}
                weight={item.page === currentPage ? 800 : 500}
              >
                {item.page! + 1}
              </Text>
            </Clickable>
          ),
        )}

        {canNextPage && (
          <IconButton icon="faChevronRight" onClick={onNextClick} />
        )}
      </div>
      <div className={classes.right}>
        {!isEmpty && (
          <Text color="textSecondary">
            {currentPage * pageSize + 1} -{" "}
            {currentPage * pageSize + currentPageSize} of {rowCount}
          </Text>
        )}
      </div>
    </div>
  );
};
